/**
 * The Venues roster's columns and sort orders (BON-538).
 *
 * Described once as data, so the header, the cells and the grid tracks all come
 * from the same filtered array — see `primitives/roster-table-types.ts`.
 *
 * CAP is seats across the venue's UPCOMING workshops, not the size of the room.
 * That is what makes `sold / cap` a forward-looking fill rate and why the two
 * sit next to each other; the room's own capacity is on the venue's detail page,
 * where it is a fact about the building rather than about the calendar.
 */
import { format, parseISO } from 'date-fns';

import { asCount } from '../../../../utils/counts';
import { byName } from '../../lib/compare';
import { FillBar } from '../../primitives/FillBar';
import type { RosterColumnDef } from '../../primitives/roster-table-types';
import type { TableSortOption } from '../../primitives/useTableSort';

import { OpenTasksChip } from './OpenTasksChip';
import { VenueRegionTag } from './VenueRegionTag';
import type { VenueRosterRow } from './venues-roster-api';

/**
 * "Mar 2026" — a partner date is scanned for the year, never the day.
 *
 * Formatted from the UTC parts. The stored value is an instant, but it is READ
 * as a calendar month, and converting to local time first moves a venue created
 * on the 1st of a month into the previous month for every reader west of UTC —
 * which is all of them.
 */
export const partnerSince = (iso: string): string => {
  const parsed = parseISO(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return format(new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), 1), 'MMM yyyy');
};

export const cityLine = (row: VenueRosterRow): string =>
  [row.city, row.state].filter(Boolean).join(', ') || '—';

/** A right-aligned count read straight off the row. */
const figureColumn = (
  id: 'capacity' | 'totalRun' | 'upcoming',
  label: string,
  width: string,
): RosterColumnDef<VenueRosterRow> => ({
  id,
  label,
  width,
  align: 'right',
  render: (row) => <span className="nb-tabular">{row[id]}</span>,
});

/** Region colour is a lookup over the whole hierarchy, so the columns are built
 *  per render rather than declared once — the array is still described in ONE
 *  place, which is what the model is for. */
export const venueColumns = (
  colorOf: (region: VenueRosterRow['region']) => string,
): RosterColumnDef<VenueRosterRow>[] => [
  {
    id: 'name',
    label: 'Venue',
    width: 'minmax(140px,1.5fr)',
    render: (row) => <span style={{ fontWeight: 600, lineHeight: 1.25 }}>{row.name}</span>,
  },
  {
    id: 'city',
    label: 'City',
    width: 'minmax(96px,0.9fr)',
    muted: true,
    render: (row) => <span style={{ lineHeight: 1.25 }}>{cityLine(row)}</span>,
  },
  {
    id: 'region',
    label: 'Region',
    width: 'minmax(136px,1.1fr)',
    render: (row) => (
      <VenueRegionTag name={row.region?.name ?? 'Unassigned'} color={colorOf(row.region)} />
    ),
  },
  figureColumn('capacity', 'Cap', '56px'),
  figureColumn('totalRun', 'Total run', '76px'),
  figureColumn('upcoming', 'Upcoming', '84px'),
  {
    id: 'sold',
    label: 'Sold / cap',
    width: 'minmax(132px,1fr)',
    render: (row) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <FillBar
          sold={row.sold}
          cap={row.capacity}
          width={72}
          ariaLabel={`${row.sold} of ${row.capacity} seats sold`}
        />
        <span className="nb-mono nb-tabular" style={{ fontSize: 11, color: 'var(--nb-muted)' }}>
          {row.sold}/{row.capacity}
        </span>
      </span>
    ),
  },
  {
    id: 'tasks',
    label: 'Tasks',
    width: 'minmax(96px,0.8fr)',
    render: (row) => <OpenTasksChip count={row.openActionItemCount} />,
  },
  {
    id: 'partnerSince',
    label: 'Partner since',
    width: '96px',
    muted: true,
    render: (row) => <span className="nb-tabular">{partnerSince(row.partnerSince)}</span>,
  },
];

/**
 * Columns that survive at tablet width. "Partner since" goes first: it is the
 * only one nobody scans a roster FOR, and it is the widest thing that is not a
 * name.
 */
export const withoutPartnerSince = (
  columns: readonly RosterColumnDef<VenueRosterRow>[],
): RosterColumnDef<VenueRosterRow>[] => columns.filter((c) => c.id !== 'partnerSince');

/**
 * FIRST is the default. Alphabetical, because the commonest reason to open this
 * page is to find a venue you can already name, and any figure order makes that
 * a scan.
 */
export const VENUE_SORTS: TableSortOption<VenueRosterRow>[] = [
  { id: 'name', label: 'Alphabetical', compare: byName },
  {
    id: 'totalRun',
    label: 'Total run',
    compare: (a, b) => b.totalRun - a.totalRun || byName(a, b),
  },
  { id: 'upcoming', label: 'Upcoming', compare: (a, b) => b.upcoming - a.upcoming || byName(a, b) },
  { id: 'sold', label: 'Seats sold', compare: (a, b) => b.sold - a.sold || byName(a, b) },
  {
    id: 'openActionItemCount',
    label: 'Open tasks',
    compare: (a, b) =>
      asCount(b.openActionItemCount) - asCount(a.openActionItemCount) || byName(a, b),
  },
  {
    id: 'fill',
    label: 'Fill rate',
    compare: (a, b) => fillRate(b) - fillRate(a) || byName(a, b),
  },
];

/** Zero capacity reads 0, not NaN — a venue with nothing upcoming sorts last. */
const fillRate = (row: VenueRosterRow): number => (row.capacity > 0 ? row.sold / row.capacity : 0);
